import { randomNonce, randomRequestId, sha256Hex } from "./crypto.js";
import { ChallengeSdkError, toChallengeError } from "./errors.js";
import { collectBrowserFingerprint } from "./fingerprint.js";
import { getDefaultStorage } from "./storage.js";
const DEFAULT_VERSION = "1";
const DEFAULT_STORAGE_KEY = "zero-trust.challenge.session";
const DEFAULT_CLIENT_VERSION = "web-sdk/0.1.0";
const MAX_REFRESH_SKEW_MS = 5_000;
const CHALLENGE_RESPONSE_CODES = new Set([
    "CHALLENGE_BAD_REQUEST",
    "CHALLENGE_EXPIRED",
    "CHALLENGE_INVALID",
    "CHALLENGE_INVALID_TIME",
    "CHALLENGE_NONCE_REPLAY",
    "CHALLENGE_POW_INVALID",
    "CHALLENGE_RATE_LIMITED",
    "CHALLENGE_SIGNATURE_INVALID",
    "CHALLENGE_UNSUPPORTED_VERSION",
]);
export function createChallengeSdk(config) {
    const normalized = normalizeConfig(config);
    let inMemorySession = readStoredSession(normalized.storage, normalized.storageKey);
    let refreshPromise = null;
    async function init() {
        return refreshChallenge(false);
    }
    async function refreshChallenge(force = false) {
        const cached = inMemorySession;
        const now = normalized.now();
        if (!force && cached && !isSessionExpired(cached, now)) {
            return cached;
        }
        if (!refreshPromise) {
            refreshPromise = loadChallengeSession()
                .then((session) => {
                inMemorySession = session;
                persistSession(normalized.storage, normalized.storageKey, session);
                normalized.onChallengeRefreshed?.(session);
                return session;
            })
                .finally(() => {
                refreshPromise = null;
            });
        }
        return refreshPromise;
    }
    function getSession() {
        if (inMemorySession && isSessionExpired(inMemorySession, normalized.now())) {
            clearSession();
            return null;
        }
        return inMemorySession;
    }
    function clearSession() {
        inMemorySession = null;
        normalized.storage.removeItem(normalized.storageKey);
    }
    async function getChallengeHeaders() {
        const session = await refreshChallenge(false);
        return createChallengeProof(session).then((proof) => proof.headers);
    }
    async function fetchWithChallenge(input, init) {
        const requestInit = init ?? {};
        if (requestInit.skipChallenge) {
            console.log("[challenge-sdk] skipChallenge", {
                input: String(input),
            });
            return normalized.businessFetch(input, stripChallengeFlags(requestInit));
        }
        if (normalized.challengeMode === "lazy" && !requestInit.forceChallenge) {
            const initialResponse = await normalized.businessFetch(input, stripChallengeFlags(requestInit));
            console.log("[challenge-sdk] initial response", {
                input: String(input),
                status: initialResponse.status,
                contentType: initialResponse.headers.get("content-type") ?? "",
            });
            const shouldHandle = await normalized.shouldHandleChallengeResponse(initialResponse.clone());
            console.log("[challenge-sdk] shouldHandleChallengeResponse", {
                input: String(input),
                shouldHandle,
            });
            if (!shouldHandle) {
                return initialResponse;
            }
        }
        console.log("[challenge-sdk] entering executeRequestWithChallenge", {
            input: String(input),
        });
        return executeRequestWithChallenge(input, requestInit);
    }
    async function executeRequestWithChallenge(input, requestInit) {
        const headers = await getChallengeHeaders();
        const response = await normalized.businessFetch(input, {
            ...stripChallengeFlags(requestInit),
            headers: mergeChallengeHeaders(requestInit.headers, headers),
        });
        const shouldRetry = normalized.retryOnChallengeFailure
            && await normalized.shouldHandleChallengeResponse(response.clone());
        if (!shouldRetry) {
            return response;
        }
        const retryHeaders = await refreshChallengeHeaders();
        return normalized.businessFetch(input, {
            ...stripChallengeFlags(requestInit),
            headers: mergeChallengeHeaders(requestInit.headers, retryHeaders),
        });
    }
    async function refreshChallengeHeaders() {
        try {
            clearSession();
            await refreshChallenge(true);
            return getChallengeHeaders();
        }
        catch (error) {
            normalized.onError?.(error);
            throw toChallengeError("Failed to refresh challenge session after gateway rejection.", "CHALLENGE_REFRESH_FAILED", error);
        }
    }
    async function loadChallengeSession() {
        try {
            console.log("[challenge-sdk] requesting challenge session", {
                challengeUrl: normalized.challengeUrl,
            });
            const response = await normalized.challengeFetch(normalized.challengeUrl, {
                method: "GET",
                headers: {
                    "X-Client-Version": normalized.clientVersion,
                    "X-Request-Id": normalized.requestIdFactory(),
                },
            });
            console.log("[challenge-sdk] challenge session response", {
                challengeUrl: normalized.challengeUrl,
                status: response.status,
                contentType: response.headers.get("content-type") ?? "",
            });
            if (!response.ok) {
                throw new ChallengeSdkError(`Challenge endpoint responded with status ${response.status}.`, "CHALLENGE_FETCH_FAILED");
            }
            const challenge = (await response.json());
            await normalized.verifyChallengeResponse?.(challenge);
            assertValidChallengeResponse(challenge);
            const fingerprint = await collectBrowserFingerprint(challenge.fingerprint_fields);
            return {
                challengeId: challenge.challenge_id,
                salt: challenge.salt,
                difficulty: challenge.difficulty,
                issuedAt: challenge.issued_at,
                expireAt: challenge.expire_at,
                nonceTtlSec: challenge.nonce_ttl_sec,
                tokenTtlSec: challenge.token_ttl_sec,
                fingerprintFields: challenge.fingerprint_fields,
                version: normalized.challengeVersion,
                fingerprintHash: fingerprint.hash,
                fingerprintRaw: fingerprint.raw,
            };
        }
        catch (error) {
            console.log("[challenge-sdk] challenge session failed", {
                challengeUrl: normalized.challengeUrl,
                error: error instanceof Error
                    ? {
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                    }
                    : String(error),
            });
            normalized.onError?.(error);
            throw toChallengeError("Failed to load challenge session.", "CHALLENGE_INIT_FAILED", error);
        }
    }
    async function createChallengeProof(session) {
        const requestTimestamp = Math.floor(normalized.now() / 1000);
        const requestNonce = randomNonce();
        const basePayloadJson = toBasePayloadJson(session, requestTimestamp);
        const powNonce = await solveProofOfWork(basePayloadJson, requestNonce, session.salt, session.difficulty);
        const tokenPayloadJson = toTokenPayloadJson(session, requestTimestamp, requestNonce, powNonce);
        const challengeKey = await sha256Hex(`${tokenPayloadJson}.${session.salt}`);
        return {
            headers: {
                "X-Challenge-Id": session.challengeId,
                "X-Challenge-Key": challengeKey,
                "X-Challenge-Time": String(requestTimestamp),
                "X-Challenge-Nonce": requestNonce,
                "X-Challenge-Pow": String(powNonce),
                "X-Challenge-Fp": session.fingerprintHash,
                "X-Challenge-Version": session.version,
            },
            tokenPayloadJson,
            basePayloadJson,
            requestTimestamp,
            requestNonce,
            powNonce,
        };
    }
    return {
        init,
        refreshChallenge,
        getSession,
        clearSession,
        getChallengeHeaders,
        fetch: fetchWithChallenge,
    };
}
function normalizeConfig(config) {
    if (!config.challengeUrl) {
        throw new ChallengeSdkError("`challengeUrl` is required.", "CONFIG_INVALID");
    }
    return {
        challengeUrl: config.challengeUrl,
        challengeMode: config.challengeMode ?? "eager",
        challengeVersion: config.challengeVersion ?? DEFAULT_VERSION,
        challengeFetch: config.challengeFetch ?? window.fetch.bind(window),
        businessFetch: config.businessFetch ?? window.fetch.bind(window),
        storage: config.storage ?? getDefaultStorage(),
        storageKey: config.storageKey ?? DEFAULT_STORAGE_KEY,
        clientVersion: config.clientVersion ?? DEFAULT_CLIENT_VERSION,
        requestIdFactory: config.requestIdFactory ?? randomRequestId,
        now: config.now ?? Date.now,
        retryOnChallengeFailure: config.retryOnChallengeFailure ?? true,
        shouldHandleChallengeResponse: config.shouldHandleChallengeResponse ?? defaultShouldHandleChallengeResponse,
        verifyChallengeResponse: config.verifyChallengeResponse,
        onChallengeRefreshed: config.onChallengeRefreshed,
        onError: config.onError,
    };
}
function readStoredSession(storage, storageKey) {
    try {
        const raw = storage.getItem(storageKey);
        if (!raw) {
            return null;
        }
        return JSON.parse(raw);
    }
    catch {
        return null;
    }
}
function persistSession(storage, storageKey, session) {
    storage.setItem(storageKey, JSON.stringify(session));
}
function isSessionExpired(session, nowMs) {
    return (session.expireAt * 1000) <= (nowMs + MAX_REFRESH_SKEW_MS);
}
function assertValidChallengeResponse(challenge) {
    if (challenge.algorithm !== "SHA-256") {
        throw new ChallengeSdkError("Unsupported challenge algorithm.", "UNSUPPORTED_ALGORITHM");
    }
    if (!challenge.challenge_id || !challenge.salt) {
        throw new ChallengeSdkError("Challenge response is missing required fields.", "CHALLENGE_INVALID");
    }
    if (challenge.expire_at <= challenge.issued_at) {
        throw new ChallengeSdkError("Challenge response has invalid expiration.", "CHALLENGE_INVALID");
    }
    if (!Number.isInteger(challenge.difficulty) || challenge.difficulty < 1) {
        throw new ChallengeSdkError("Challenge difficulty must be a positive integer.", "CHALLENGE_INVALID");
    }
}
function toBasePayloadJson(session, requestTimestamp) {
    return JSON.stringify({
        challenge_id: session.challengeId,
        ts: requestTimestamp,
        fp: session.fingerprintHash,
        version: session.version,
    });
}
function toTokenPayloadJson(session, requestTimestamp, requestNonce, powNonce) {
    return JSON.stringify({
        challenge_id: session.challengeId,
        ts: requestTimestamp,
        nonce: requestNonce,
        fp: session.fingerprintHash,
        pow_nonce: powNonce,
        version: session.version,
    });
}
async function solveProofOfWork(basePayloadJson, requestNonce, salt, difficulty) {
    const prefix = "0".repeat(difficulty);
    for (let powNonce = 0; powNonce <= Number.MAX_SAFE_INTEGER; powNonce += 1) {
        const hash = await sha256Hex(`${basePayloadJson}.${requestNonce}.${salt}.${powNonce}`);
        if (hash.startsWith(prefix)) {
            return powNonce;
        }
    }
    throw new ChallengeSdkError("Unable to solve proof of work.", "POW_EXHAUSTED");
}
function mergeChallengeHeaders(existingHeaders, challengeHeaders) {
    const headers = new Headers(existingHeaders ?? undefined);
    for (const [key, value] of Object.entries(challengeHeaders)) {
        headers.set(key, value);
    }
    return headers;
}
function stripChallengeFlags(init) {
    const rest = { ...init };
    delete rest.skipChallenge;
    delete rest.forceChallenge;
    return rest;
}
async function defaultShouldHandleChallengeResponse(response) {
    const contentType = response.headers.get("content-type") ?? "";
    if (response.status !== 400 && response.status !== 401 && response.status !== 403) {
        console.log("[challenge-sdk] ignore response: status not challenge", {
            status: response.status,
            contentType,
        });
        return false;
    }
    if (!contentType.toLowerCase().includes("application/json")) {
        console.log("[challenge-sdk] ignore response: non-json content-type", {
            status: response.status,
            contentType,
        });
        return false;
    }
    try {
        const body = (await response.json());
        const shouldHandle = typeof body.code === "string" && CHALLENGE_RESPONSE_CODES.has(body.code);
        console.log("[challenge-sdk] parsed challenge response", {
            status: response.status,
            contentType,
            body,
            shouldHandle,
        });
        return shouldHandle;
    }
    catch (error) {
        console.log("[challenge-sdk] response json parse failed", {
            status: response.status,
            contentType,
            error: error instanceof Error ? error.message : String(error),
        });
        return false;
    }
}
